import { type PropsWithChildren, type ReactNode, useState } from 'react';
import { capitalize } from 'es-toolkit';
import { motion, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import '../tracking/events.quiz-school-pricing';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useOpenWindow } from '@/hooks';
import { BoltSmallRectangleColorMintIcon, BoltSmallRectangleColorVioletIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { usePrices } from '@/module/payment/hooks/usePrices';
import { getInquiryLinkByCountryCode } from '@/module/plan/consts/inquiryLinks';
import { RestrictedPlanDialog } from '@/shared/components/UpsellingModal/RestrictedPlanDialog';
import { resolveUpsellingPlanState } from '@/shared/components/UpsellingModal/upsellingPlanState';
import { getUpsellingCardCta, type PaidPlanType, type UpsellingCtaAction } from '@/shared/components/UpsellingModal/upsellingPolicy';
import { useCountryCode, useInquiryUrl } from '@/shared/hooks';
import { formatCurrencyPrice } from '@/shared/utils/currency';
import { type PlanType } from '@/types';
import { Button } from '@/ui_v3';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { cn } from '@/utils/tailwind.util';
import { useCurrentPlanChangeGuard, usePaymentFlow } from '../../shared';
import { getPaymentLoginUrl } from '../../shared/utils/paymentEntryUrl';
import { type GroupPricingContent, type GroupPricingPlan } from '../PricingSection1/groupPricingContent';
import { PRICING_TAB_QUERY_KEY } from '../PricingSection1/pricingTabQuery';
const AUDIENCE_SEGMENT_LAYOUT_ID = 'pricing-audience-segment-active-surface';
const AUDIENCE_SEGMENT_TRANSITION = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1]
} as const;
type Audience = 'individual' | 'group';
export const TableHeader = ({
  groupPricingContent
}: {
  groupPricingContent: GroupPricingContent;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    isAuthenticated,
    user
  } = useUser();
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const paymentCountryCode = user?.countryCode ?? countryCode;
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const openWindow = useOpenWindow();
  const planState = resolveUpsellingPlanState(user);
  const isPlanChangeBlocked = useCurrentPlanChangeGuard({
    enabled: planState.kind === 'individual'
  });
  const basicPrices = usePrices({
    countryCode,
    planType: 'BASIC'
  });
  const basicPriceText = formatCurrencyPrice(basicPrices.displayFinalPrice, basicPrices.displayCurrency);
  const proPrices = usePrices({
    countryCode,
    planType: 'PRO'
  });
  const proPriceText = formatCurrencyPrice(proPrices.displayFinalPrice, proPrices.displayCurrency);
  const {
    openInquiryUrl
  } = useInquiryUrl({
    krUrl: '/embed/sales?type=inquiry',
    source: 'pricing'
  });
  const [basicAudience, setBasicAudience] = useState<Audience>('group');
  const [proAudience, setProAudience] = useState<Audience>('group');
  const resolvedBasicAudience = isKR ? 'individual' : basicAudience;
  const ctaLabels: Record<UpsellingCtaAction, string> = {
    purchase: t('common:pricing.pro.purchase'),
    'select-plan': t('common:pricing.pro.purchase'),
    extend: t('common:pricing.extendPeriod'),
    upgrade: t('common:pricing.pro.upgradeToPro'),
    'higher-plan-active': t('common:pricing.proPlanInUse'),
    'current-plan': t('common:upselling.currentlyUsing'),
    inquiry: t('common:pricing.pro.inquireNow')
  };
  const basicCta = resolvedBasicAudience === 'individual' ? getUpsellingCardCta({
    state: planState,
    targetPlan: 'BASIC',
    intent: 'NEW_PURCHASE'
  }) : undefined;
  const proCta = proAudience === 'individual' ? getUpsellingCardCta({
    state: planState,
    targetPlan: 'PRO',
    intent: 'NEW_PURCHASE'
  }) : undefined;
  const basicPrice = getPlanPricePresentation({
    audience: resolvedBasicAudience,
    groupPlan: groupPricingContent.basic,
    individualPriceText: basicPriceText,
    currency: basicPrices.displayCurrency,
    inquiryText: t('pricing:schoolGroup.inquiry')
  });
  const proPrice = getPlanPricePresentation({
    audience: proAudience,
    groupPlan: groupPricingContent.pro,
    individualPriceText: proPriceText,
    currency: proPrices.displayCurrency,
    inquiryText: t('pricing:schoolGroup.inquiry')
  });
  // Basic/Pro 가 훅 하나를 공유하므로 클릭 이벤트는 CTA 별로 콜백에서 발화한다
  // (훅 설정에 두면 어느 쪽을 눌러도 같은 이벤트가 찍힌다).
  const handleBasicGroupInquiry = () => {
    void openInquiryUrl({
      location: 'pricing_table_basic',
      onInquiryClick: tracking_id => trackEvent('quiz_pricing_basic_inquiry_click', {
        tracking_id
      })
    });
  };
  const handleProGroupInquiry = () => {
    void openInquiryUrl({
      location: 'pricing_table_pro',
      onInquiryClick: tracking_id => trackEvent('click_school_pro_inquiry', {
        tracking_id
      })
    });
  };
  const startPurchase = (plan: PaidPlanType) => {
    const entry = {
      kind: 'purchase' as const,
      plan
    };
    if (!isAuthenticated) {
      openWindow(getPaymentLoginUrl(entry, paymentCountryCode));
      return;
    }
    startPaymentFlow({
      ...entry,
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };
  const executeIndividualCta = (plan: PaidPlanType, action: UpsellingCtaAction) => {
    switch (action) {
      case 'purchase':
        startPurchase(plan);
        return;
      case 'select-plan':
        overlay.open(({
          unmount
        }) => <RestrictedPlanDialog onClose={unmount} />);
        return;
      case 'extend':
      case 'upgrade':
        if (isPlanChangeBlocked(action)) return;
        startPaymentFlow({
          kind: action,
          target: {
            kind: 'active'
          },
          countryCode: paymentCountryCode
        }, {
          target: 'new-tab'
        });
        return;
      case 'inquiry':
        window.open(getInquiryLinkByCountryCode(paymentCountryCode), '_blank', 'noopener,noreferrer');
        return;
      case 'current-plan':
      case 'higher-plan-active':
        return;
    }
  };
  const handleIndividualCta = (plan: PaidPlanType, action: UpsellingCtaAction) => {
    // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
    logger.track('click_pricing_table_cta', {
      planType: plan
    });
    executeIndividualCta(plan, action);
  };
  const handleSecondaryPurchase = (plan: PaidPlanType) => {
    // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
    logger.track('click_pricing_table_cta', {
      planType: plan
    });
    startPurchase(plan);
  };
  return <div className="grid grid-cols-3 gap-x-lg laptop-sm:grid-cols-4" data-sentry-component="TableHeader" data-sentry-source-file="TableHeader.tsx">
      <div className="hidden min-w-[160px] laptop-sm:block" />
      <PlanTypeColumn planType="FREE" priceText={t('common:pricing.free.free')} ctaText={t('common:pricing.free.startForFree')} toggleSlot={<AudienceSegmentBarPlaceholder />} data-sentry-element="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx" />
      <PlanTypeColumn planType="BASIC" priceText={basicPrice.text} ctaText={basicCta ? ctaLabels[basicCta.action] : t('pricing:schoolGroup.inquiry')} ctaButtonDisabled={basicCta?.disabled} showMonthlySuffix={!basicPrice.isInquiry} toggleSlot={isKR ? <AudienceSegmentBarPlaceholder /> : <AudienceSegmentBar audience={basicAudience} layoutIdPrefix="basic" onChange={setBasicAudience} />} onCtaClick={basicCta ? () => handleIndividualCta('BASIC', basicCta.action) : resolvedBasicAudience === 'group' ? handleBasicGroupInquiry : undefined} secondaryCtaText={basicCta?.hasSecondaryPurchase ? t('common:detail.planPurchase') : undefined} onSecondaryCtaClick={() => handleSecondaryPurchase('BASIC')} data-sentry-element="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx" />
      <PlanTypeColumn planType="PRO" priceText={proPrice.text} ctaText={proCta ? ctaLabels[proCta.action] : t('pricing:schoolGroup.inquiry')} ctaButtonDisabled={proCta?.disabled} showMonthlySuffix={!proPrice.isInquiry} toggleSlot={<AudienceSegmentBar audience={proAudience} layoutIdPrefix="pro" onChange={setProAudience} />} onCtaClick={proCta ? () => handleIndividualCta('PRO', proCta.action) : proAudience === 'group' ? handleProGroupInquiry : undefined} secondaryCtaText={proCta?.hasSecondaryPurchase ? t('common:detail.planPurchase') : undefined} onSecondaryCtaClick={() => handleSecondaryPurchase('PRO')} data-sentry-element="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx" />
    </div>;
};
interface PlanTypeColumnProps {
  planType: PlanType;
  priceText: string;
  ctaText: string;
  /** 가격 위 토글 슬롯. Pro=개인/단체 토글, Free/Basic=정렬용 placeholder. */
  toggleSlot?: ReactNode;
  showMonthlySuffix?: boolean;
  /** CTA 클릭 동작 override(예: 단체 Pro=견적 문의). 없으면 기본 플랜별 동작. */
  onCtaClick?: () => void;
  ctaButtonDisabled?: boolean;
  secondaryCtaText?: string;
  onSecondaryCtaClick?: () => void;
}
const PlanTypeColumn = ({
  planType,
  priceText,
  ctaText,
  toggleSlot,
  showMonthlySuffix = true,
  onCtaClick,
  ctaButtonDisabled = false,
  secondaryCtaText,
  onSecondaryCtaClick
}: PlanTypeColumnProps) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const router = useRouter();
  const {
    isAuthenticated
  } = useUser();
  const {
    countryCode
  } = useCountryCode();
  const openWindow = useOpenWindow();
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const isPaid = planType !== 'FREE';
  return <div className="flex min-w-[160px] flex-col items-start gap-xs px-xs py-md" data-sentry-component="PlanTypeColumn" data-sentry-source-file="TableHeader.tsx">
      <div className="flex items-center gap-2xs">
        {planType === 'BASIC' && <BoltSmallRectangleColorMintIcon size="md" />}
        {planType === 'PRO' && <BoltSmallRectangleColorVioletIcon size="md" />}
        <strong className="text-heading-xl-extrabold">
          {capitalize(planType)}
        </strong>
      </div>

      {toggleSlot}

      <div className="flex items-center gap-3xs pb-sm">
        <span className="text-body-lg-semibold">{priceText}</span>
        {isPaid && showMonthlySuffix && <span className="text-body-sm-medium text-alternative">
            {`/${t('pricing:perMonths', {
          n: 1
        })}`}
          </span>}
      </div>

      <div className="flex flex-col gap-2xs">
        <Button size="md" variant={isPaid ? 'contained' : 'outlined'} intent={(() => {
        switch (planType) {
          case 'FREE':
            return 'neutral';
          case 'BASIC':
            return 'alternative';
          case 'PRO':
            return 'gradient';
        }
      })()} disabled={ctaButtonDisabled} onClick={() => {
        if (onCtaClick) {
          onCtaClick();
          return;
        }
        logger.track('click_pricing_table_cta', {
          // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
          planType
        });
        if (planType === 'FREE') {
          void router.push(isAuthenticated ? '/build' : combinePathAndQuery('/login', router.query, [PRICING_TAB_QUERY_KEY]));
        } else {
          if (!isAuthenticated) {
            openWindow(getPaymentLoginUrl({
              kind: 'purchase',
              plan: planType
            }, countryCode));
            return;
          }
          startPaymentFlow({
            kind: 'purchase',
            plan: planType,
            countryCode
          }, {
            target: 'new-tab'
          });
        }
      }} data-sentry-element="Button" data-sentry-source-file="TableHeader.tsx">
          {ctaText}
        </Button>
        {secondaryCtaText && <Button size="md" variant="outlined" intent="neutral" onClick={onSecondaryCtaClick}>
            {secondaryCtaText}
          </Button>}
      </div>
    </div>;
};
const AudienceSegmentBar = ({
  audience,
  layoutIdPrefix,
  onChange
}: {
  audience: Audience;
  layoutIdPrefix: 'basic' | 'pro';
  onChange: (audience: Audience) => void;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const individualLabel = t('pricing:schoolGroup.individual');
  const groupLabel = t('pricing:schoolGroup.group');
  return <div role="group" aria-label={t('pricing:schoolGroup.audienceLabel')} className="flex h-9 w-fit min-w-[94px] max-w-full items-center rounded-full bg-neutral shadow-[inset_0_0_0_1px_rgba(28,27,31,0.04)]" data-sentry-component="AudienceSegmentBar" data-sentry-source-file="TableHeader.tsx">
      <AudienceSegment active={audience === 'individual'} layoutId={`${AUDIENCE_SEGMENT_LAYOUT_ID}-${layoutIdPrefix}`} sizingLabel={groupLabel} onClick={() => onChange('individual')} data-sentry-element="AudienceSegment" data-sentry-source-file="TableHeader.tsx">
        {individualLabel}
      </AudienceSegment>
      <AudienceSegment active={audience === 'group'} layoutId={`${AUDIENCE_SEGMENT_LAYOUT_ID}-${layoutIdPrefix}`} sizingLabel={individualLabel} onClick={() => onChange('group')} data-sentry-element="AudienceSegment" data-sentry-source-file="TableHeader.tsx">
        {groupLabel}
      </AudienceSegment>
    </div>;
};

// Free/Basic 정렬용 placeholder. 실제 토글과 같은 높이만 차지하고 포커스 대상은 만들지 않는다.
const AudienceSegmentBarPlaceholder = () => <div className="h-9" aria-hidden data-sentry-component="AudienceSegmentBarPlaceholder" data-sentry-source-file="TableHeader.tsx" />;
const AudienceSegment = ({
  active,
  layoutId,
  sizingLabel,
  onClick,
  children
}: PropsWithChildren<{
  active: boolean;
  layoutId: string;
  sizingLabel: string;
  onClick: () => void;
}>) => {
  const shouldReduceMotion = useReducedMotion();
  return <button type="button" aria-pressed={active} onClick={onClick} className={cn('relative flex h-9 min-w-[47px] flex-1 items-center justify-center whitespace-nowrap rounded-full px-sm text-body-sm-semibold transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-strong focus-visible:ring-offset-1', active ? 'text-default' : 'text-assistive hover:text-neutral')} data-sentry-component="AudienceSegment" data-sentry-source-file="TableHeader.tsx">
      {active && <motion.span layoutId={layoutId} className="absolute inset-0 rounded-full bg-default shadow-[inset_0_0_0_1px_rgba(28,27,31,0.04),0_0_10px_0_rgba(42,39,65,0.08),0_3px_6px_0_rgba(42,39,65,0.08)]" transition={shouldReduceMotion ? {
      duration: 0
    } : AUDIENCE_SEGMENT_TRANSITION} />}
      <span className="relative grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)] overflow-hidden">
        <span className="col-start-1 row-start-1 min-w-0 truncate">
          {children}
        </span>
        <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-nowrap">
          {sizingLabel}
        </span>
      </span>
    </button>;
};
const getPlanPricePresentation = ({
  audience,
  currency,
  groupPlan,
  individualPriceText,
  inquiryText
}: {
  audience: Audience;
  currency: string;
  groupPlan: GroupPricingPlan;
  individualPriceText: string;
  inquiryText: string;
}) => {
  if (audience === 'individual') {
    return {
      isInquiry: false,
      text: individualPriceText
    };
  }
  if (groupPlan.monthlyPricePerPerson.type === 'inquiry') {
    return {
      isInquiry: true,
      text: inquiryText
    };
  }
  return {
    isInquiry: false,
    text: formatCurrencyPrice(groupPlan.monthlyPricePerPerson.value, currency)
  };
};